import React, { useEffect, useState, useMemo } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ListingCard from "@/components/listing/ListingCard";
import SEO from "@/components/SEO";
import {
  MapPin, ArrowRight, ShieldCheck, Sparkles, HelpCircle, Globe2, Building2,
  Shirt, Sparkle, UtensilsCrossed, Home as HomeIcon, Gift, Users, ChevronRight,
} from "lucide-react";
import { findCountry, findCity, findArea } from "@/data/locations";
import { getAllCities, findCityExtended, findAreaExtended } from "@/data/cities";

const CATEGORY_CHIPS = [
  { slug: "fashion", name: "Fashion", icon: Shirt, cat: "Fashion & Ankara" },
  { slug: "beauty", name: "Beauty & Hair", icon: Sparkle, cat: "Beauty & Hair" },
  { slug: "food", name: "Food", icon: UtensilsCrossed, cat: "Food & Spices" },
  { slug: "home", name: "Home", icon: HomeIcon, cat: "Art & Crafts" },
  { slug: "gifts", name: "Gifts", icon: Gift, cat: "Jewelry" },
  { slug: "services", name: "Local Services", icon: Users, cat: null, type: "service" },
];

const LocationLanding = () => {
  const location = useLocation();
  const segments = location.pathname.replace(/^\/+|\/+$/g, "").split("/");
  const [countrySlug, citySlug, areaSlug] = segments;

  const country = findCountry(countrySlug);

  // Merge the comprehensive city list (cities.js) with the curated detail (locations.js)
  const allCities = country ? getAllCities(countrySlug) : [];
  const curatedCityFromLocations = citySlug ? findCity(countrySlug, citySlug) : null;
  const extendedCity = citySlug ? findCityExtended(countrySlug, citySlug) : null;
  // Use curated if present (has nice blurbs), otherwise the comprehensive one
  const city = curatedCityFromLocations || (extendedCity ? { ...extendedCity, country } : null);

  const curatedAreaFromLocations = areaSlug ? findArea(countrySlug, citySlug, areaSlug) : null;
  const extendedArea = areaSlug ? findAreaExtended(countrySlug, citySlug, areaSlug) : null;
  const area = curatedAreaFromLocations || (extendedArea && city ? { ...extendedArea, city, country } : null);

  const [citySearch, setCitySearch] = useState("");

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Determine current level
  const level = area ? "area" : city ? "city" : country ? "country" : null;
  const node = area || city || country;

  // Backend filter params for this location (primitives so React lint is happy)
  const apiCountry = country?.apiCountry;
  const apiCity = city?.apiCity || city?.name;
  const apiRegion = area?.name;

  useEffect(() => {
    if (!apiCountry) return;
    const params = { limit: 24, country: apiCountry };
    if (apiCity) params.city = apiCity;
    if (apiRegion) params.region = apiRegion;
    setLoading(true);
    api.get("/listings", { params })
      .then((r) => setListings(r.data || []))
      .finally(() => setLoading(false));
  }, [apiCountry, apiCity, apiRegion]);

  if (!country) return <Navigate to="/locations" replace />;

  // ── SEO content ─────────────────────────────────────────────────────────
  const locationLabel = area
    ? `${area.name}, ${city.name}`
    : city
    ? `${city.name}, ${country.name}`
    : country.name;

  const h1 = area
    ? `African Businesses, Food & Services in ${area.name}, ${city.name}`
    : city
    ? `Shop & Book African Businesses in ${city.name}, ${country.name}`
    : `${country.flag} Shop African Businesses in ${country.name}`;

  const metaTitle = area
    ? `African Sellers & Services in ${area.name}, ${city.name}`
    : city
    ? `African Marketplace in ${city.name} — Products, Food, Services`
    : `${country.name} on AfriMarket — Authentic African Sellers`;

  const metaDesc = area
    ? `Find African food, braiders, caterers, tailors, fashion and local services in ${area.name}, ${city.name}. Verified sellers, secure payments, real human reviews on AfriMarket.`
    : city
    ? `Discover the African marketplace of ${city.name}, ${country.name}. Local braiders, caterers, fashion designers, beauty brands, restaurants and event services — all verified on AfriMarket.`
    : country.intro.slice(0, 158);

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Locations", path: "/locations" },
    { name: country.name, path: `/${countrySlug}` },
    ...(city ? [{ name: city.name, path: `/${countrySlug}/${citySlug}` }] : []),
    ...(area ? [{ name: area.name, path: `/${countrySlug}/${citySlug}/${areaSlug}` }] : []),
  ];

  // Dynamic Open Graph image — branded card per location for richer social
  // previews (WhatsApp / Twitter / Facebook / LinkedIn).
  const ogImage = (() => {
    const params = new URLSearchParams({ country: countrySlug });
    if (citySlug) params.set("city", citySlug);
    if (areaSlug) params.set("area", areaSlug);
    return `https://afrimarket.org/api/og/location.png?${params.toString()}`;
  })();

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((b, i) => ({
      "@type": "ListItem", "position": i + 1, "name": b.name, "item": `https://afrimarket.org${b.path}`,
    })),
  };

  const collectionJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": h1,
    "description": metaDesc,
    "url": `https://afrimarket.org${location.pathname}`,
  };

  // ── FAQs (auto-generated from location level) ───────────────────────────
  const buildFaqs = () => {
    if (area) {
      return [
        { q: `Are there African sellers in ${area.name}?`, a: `Yes. AfriMarket has verified African sellers offering products, food and services in ${area.name} and the wider ${city.name} area. Browse the listings above or use the category filters to narrow your search.` },
        { q: `Where can I find African food in ${area.name}?`, a: `Use the 'Food' category filter on this page to see African food sellers — including jollof caterers, fresh ingredients, snacks and home-cooked meals delivered around ${area.name}.` },
        { q: `Can I book a braider or hairdresser in ${area.name}?`, a: `Yes. The 'Local Services' category on this page shows verified braiders, hairstylists and beauty professionals offering mobile or salon-based appointments in ${area.name}.` },
        { q: `Are AfriMarket sellers in ${area.name} verified?`, a: `Every seller on AfriMarket completes an ID and proof-of-address check before they can sell. Verified sellers carry a blue badge — visible in every listing.` },
        { q: `What if a service or order goes wrong?`, a: `Open a case in our Resolution Centre within 7 days. Funds are held in escrow until you confirm satisfaction, so you're always protected.` },
      ];
    }
    if (city) {
      return [
        { q: `Are there many African sellers in ${city.name}?`, a: `Yes. ${city.name} is one of the most active locations on AfriMarket. ${city.blurb || ""} Use the category chips above to find exactly what you're looking for.` },
        { q: `Can I find local services in ${city.name}?`, a: `Absolutely. Use the 'Local Services' filter to find braiders, caterers, photographers, childminders, tailors and event professionals across ${city.name}.` },
        { q: `Where can I find African food in ${city.name}?`, a: `${city.name} has dozens of African food and grocery sellers on AfriMarket. Tap the 'Food' filter or search for specific items like jollof, suya or shea butter.` },
        { q: `Do sellers in ${city.name} ship outside the city?`, a: `Many do — most listings show shipping coverage. Some local services are mobile within a 10-15 mile radius of ${city.name}, while products from ${city.name} sellers ship across ${country.name} and internationally.` },
        { q: `How do I become a seller in ${city.name}?`, a: `Click 'Become a Seller', verify your ID and proof of address, and start listing within 24-48 hours. AfriMarket charges a small commission only when you make a sale — listing is free.` },
      ];
    }
    return [
      { q: `Why shop from ${country.name} sellers?`, a: `${country.intro}` },
      { q: `Do ${country.name} sellers ship internationally?`, a: `Many ${country.name}-based sellers ship to the UK, US, Canada and across Europe. Delivery times typically range from 5-14 business days, with tracking provided.` },
      { q: `How are sellers in ${country.name} verified?`, a: `Every ${country.name} seller completes an ID and proof-of-address check before they can list. Verified sellers carry the AfriMarket blue badge.` },
      { q: `What if my ${country.name} order doesn't arrive?`, a: `Funds are held in escrow until you confirm receipt. If your item never arrives or doesn't match the listing, open a case in our Resolution Centre for a full refund.` },
      { q: `Are payments to ${country.name} sellers safe?`, a: `Yes. All payments go through Stripe and PayPal escrow. Sellers in ${country.name} receive payouts only after buyer satisfaction is confirmed.` },
      { q: `How do I sell on AfriMarket from ${country.name}?`, a: `Sign up free, complete verification, list your products or services and start receiving orders from the African diaspora worldwide. AfriMarket handles payments, escrow and dispute resolution for you.` },
    ];
  };
  const faqs = buildFaqs();

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  // ── Category filter UI ──────────────────────────────────────────────────
  const browseUrl = (extra = {}) => {
    const params = new URLSearchParams();
    params.set("country", country.apiCountry);
    if (city) params.set("city", city.apiCity || city.name);
    if (area) params.set("region", area.name);
    for (const [k, v] of Object.entries(extra)) params.set(k, v);
    return `/browse?${params.toString()}`;
  };

  return (
    <div className="heritage-canvas">
      <SEO
        title={metaTitle}
        description={metaDesc}
        path={location.pathname}
        image={ogImage}
        jsonLd={[collectionJsonLd, breadcrumbJsonLd, faqJsonLd]}
      />

      {/* Breadcrumbs */}
      <div className="container-narrow py-4 text-sm text-muted-foreground flex items-center gap-1 flex-wrap" data-testid="breadcrumbs">
        {breadcrumbs.map((b, i) => (
          <React.Fragment key={b.path}>
            {i > 0 && <ChevronRight className="w-3 h-3" />}
            {i === breadcrumbs.length - 1 ? (
              <span className="text-foreground font-medium">{b.name}</span>
            ) : (
              <Link to={b.path} className="hover:text-[#C05A3C]">{b.name}</Link>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* HERO */}
      <section className="container-narrow pb-8">
        <div className="bg-gradient-to-br from-[#1A2024] via-[#233348] to-[#2A3D36] rounded-3xl p-8 lg:p-12 text-white relative overflow-hidden">
          <div className="absolute inset-0 ankara-stripe opacity-10" aria-hidden />
          <div className="relative">
            <div className="label-overline text-[#F2C94C] mb-3 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> {country.type === "africa" ? "Shop from Africa" : "Local services & businesses"}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="location-h1">
              {h1}
            </h1>
            <p className="mt-5 text-lg text-white/85 max-w-2xl leading-relaxed">
              {area
                ? `Verified African sellers offering food, fashion, beauty, services and culture in ${area.name} and across ${city.name}. Shop with confidence — backed by our buyer guarantee.`
                : city
                ? city.blurb || `Discover the African marketplace of ${city.name}. From local services to specialty products, every seller is verified and every transaction protected.`
                : country.intro}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to={browseUrl()} data-testid="hero-browse-all">
                <Button className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md h-12 px-6 font-semibold">
                  Browse all in {node.name} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/register" data-testid="hero-become-seller">
                <Button variant="outline" className="border-white/40 text-white hover:bg-white hover:text-[#1A2024] rounded-md h-12 px-6 font-semibold">
                  Sell in {node.name}
                </Button>
              </Link>
            </div>
            <div className="mt-5 flex items-center gap-2 text-sm text-white/70">
              <ShieldCheck className="w-4 h-4" />
              <span>Verified sellers · Escrow payments · 7-day buyer guarantee</span>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY CHIPS — works at any level */}
      <section className="container-narrow pb-6">
        <div className="label-overline text-[#C05A3C] mb-3">Shop by category in {node.name}</div>
        <div className="flex flex-wrap gap-2" data-testid="category-chips">
          {CATEGORY_CHIPS.map((c) => {
            const params = c.type ? { type: c.type } : { category: c.cat };
            return (
              <Link
                key={c.slug}
                to={browseUrl(params)}
                className="inline-flex items-center gap-2 bg-white border border-black/10 rounded-full px-4 py-2 text-sm hover:border-[#C05A3C] hover:text-[#C05A3C] transition-colors"
                data-testid={`chip-${c.slug}`}
              >
                <c.icon className="w-4 h-4" />
                {c.name} in {node.name}
              </Link>
            );
          })}
        </div>
      </section>

      {/* SUB-LOCATIONS DRILL-DOWN */}
      {level === "country" && allCities.length > 0 && (
        <section className="container-narrow py-10" data-testid="cities-drill">
          <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="label-overline text-[#C05A3C] mb-2">Cities & regions</div>
              <h2 className="font-display text-3xl">Explore {country.name} — all {allCities.length} cities</h2>
            </div>
            <input
              type="search"
              data-testid="city-search-input"
              placeholder={`Search ${allCities.length} cities…`}
              value={citySearch}
              onChange={(e) => setCitySearch(e.target.value)}
              className="bg-white border border-black/10 rounded-md px-4 py-2 text-sm w-full sm:w-72 focus:border-[#C05A3C] outline-none"
            />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2" data-testid="cities-grid">
            {allCities
              .filter((c) => !citySearch || c.name.toLowerCase().includes(citySearch.toLowerCase()))
              .map((c) => (
                <Link
                  key={c.slug}
                  to={`/${countrySlug}/${c.slug}`}
                  data-testid={`city-link-${c.slug}`}
                  className="group bg-white border border-black/10 rounded-lg px-3 py-2 hover:border-[#C05A3C] hover:shadow-sm transition-all flex items-center gap-2"
                >
                  <Building2 className="w-3.5 h-3.5 text-[#C05A3C] shrink-0" />
                  <span className="text-sm font-medium group-hover:text-[#C05A3C] transition-colors truncate">{c.name}</span>
                  {c.areas?.length > 0 && (
                    <span className="ml-auto text-[10px] text-muted-foreground shrink-0">{c.areas.length} areas</span>
                  )}
                </Link>
              ))}
          </div>
        </section>
      )}

      {level === "city" && city?.areas && city.areas.length > 0 && (
        <section className="container-narrow py-10" data-testid="areas-drill">
          <div className="label-overline text-[#C05A3C] mb-3">Areas in {city.name}</div>
          <h2 className="font-display text-3xl mb-6">Find sellers near your neighbourhood</h2>
          <div className="flex flex-wrap gap-2">
            {city.areas.map((a) => (
              <Link
                key={a.slug}
                to={`/${countrySlug}/${citySlug}/${a.slug}`}
                data-testid={`area-link-${a.slug}`}
                className="inline-flex items-center gap-1 bg-[#FBF6EE] border border-black/10 rounded-full px-4 py-2 text-sm hover:border-[#C05A3C] hover:text-[#C05A3C] transition-colors"
              >
                <MapPin className="w-3.5 h-3.5" /> {a.name}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* LIVE LISTINGS */}
      <section className="container-narrow py-10" data-testid="listings-section">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="label-overline text-[#C05A3C] mb-1">Available now</div>
            <h2 className="font-display text-3xl tracking-tight">Live listings in {node.name}</h2>
          </div>
          <Link to={browseUrl()} className="text-sm font-medium hover:text-[#C05A3C] inline-flex items-center gap-1" data-testid="see-all-listings">
            See all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {loading ? (
          <div className="text-sm text-muted-foreground py-12 text-center">Loading listings near {node.name}...</div>
        ) : listings.length === 0 ? (
          <Card className="p-8 text-center bg-white border-black/10">
            <Globe2 className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No live listings in {node.name} yet. <Link to="/register" className="text-[#C05A3C] font-semibold">Be the first to sell here →</Link>
            </p>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.slice(0, 9).map((l) => <ListingCard key={l.listing_id} listing={l} />)}
          </div>
        )}
      </section>

      {/* WHY SHOP HERE */}
      <section className="bg-[#FBF6EE] border-y border-black/5">
        <div className="container-narrow py-12 max-w-3xl">
          <div className="label-overline text-[#C05A3C] mb-2">Why buy from {node.name}?</div>
          <h2 className="font-display text-3xl mb-5">
            {country.type === "africa"
              ? "Directly from the source — authentic, fair-trade, and supporting African entrepreneurs."
              : `Local to you. Loved by the diaspora. Trusted by ${node.name}.`}
          </h2>
          <div className="space-y-3 text-base leading-relaxed text-[#1A1A1A]">
            <p>
              {country.type === "africa"
                ? `${country.name} is one of Africa's most vibrant marketplaces, and AfriMarket connects you directly to its makers. Skip the middlemen, fast-fashion factories and white-label resellers — every seller here is independently verified and earns the full value of what they make.`
                : `The African diaspora in ${node.name} is large, vibrant and full of incredible businesses — but finding them has always been hard. AfriMarket changes that. Every braider, caterer, tailor and designer is verified, reviewed and easy to book.`}
            </p>
            <p>
              {`Whether you're shopping for products you can't get on the high street, booking a service from someone who shares your culture, or just supporting Black-owned businesses — AfriMarket makes ${node.name} discoverable, trustworthy and convenient.`}
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-narrow py-14 max-w-3xl">
        <div className="text-center mb-8">
          <div className="label-overline text-[#C05A3C] mb-2 flex items-center justify-center gap-2">
            <HelpCircle className="w-3.5 h-3.5" /> FAQ
          </div>
          <h2 className="font-display text-3xl tracking-tight">Frequently Asked Questions about {locationLabel}</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((item, idx) => (
            <details
              key={item.q}
              data-testid={`faq-${idx}`}
              className="group bg-white rounded-2xl border border-black/10 p-5 hover:border-[#C05A3C] transition-colors"
            >
              <summary className="cursor-pointer list-none flex items-center justify-between font-display text-lg font-semibold gap-4">
                <span>{item.q}</span>
                <ArrowRight className="w-4 h-4 text-[#C05A3C] transition-transform group-open:rotate-90 shrink-0" />
              </summary>
              <p className="mt-3 text-muted-foreground leading-relaxed">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* SIBLING / SISTER LOCATIONS */}
      {level !== "country" && (
        <section className="container-narrow pb-14">
          <div className="label-overline text-[#C05A3C] mb-3">Also explore</div>
          <h2 className="font-display text-3xl mb-6">More of {country.name}</h2>
          <div className="flex flex-wrap gap-2">
            {allCities
              .filter((c) => c.slug !== citySlug)
              .slice(0, 12)
              .map((c) => (
                <Link
                  key={c.slug}
                  to={`/${countrySlug}/${c.slug}`}
                  className="inline-flex items-center gap-1 bg-white border border-black/10 rounded-full px-4 py-2 text-sm hover:border-[#C05A3C] hover:text-[#C05A3C] transition-colors"
                >
                  <MapPin className="w-3.5 h-3.5" /> {c.name}
                </Link>
              ))}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="container-narrow pb-16 pt-4">
        <Card className="bg-[#C05A3C] text-white rounded-2xl border-0 p-8 lg:p-12 relative overflow-hidden">
          <div className="absolute inset-0 ankara-stripe opacity-20" aria-hidden />
          <div className="relative grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="label-overline text-white/80 mb-2 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Become discoverable
              </div>
              <h2 className="font-display text-3xl leading-tight">
                Selling from {node.name}? Reach the global African diaspora.
              </h2>
              <p className="mt-3 text-white/85 leading-relaxed">
                List free. Get verified. Receive orders from buyers around the world — backed by AfriMarket&apos;s escrow and dispute protection.
              </p>
            </div>
            <div className="md:text-right space-y-3">
              <Link to="/register" data-testid="final-cta-sell">
                <Button className="bg-white text-[#C05A3C] hover:bg-white/90 rounded-md h-12 px-8 font-semibold mr-3">
                  Become a Seller
                </Button>
              </Link>
              <Link to={browseUrl()} data-testid="final-cta-browse">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#C05A3C] rounded-md h-12 px-8 font-semibold">
                  Browse all
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      </section>
    </div>
  );
};

export default LocationLanding;
